import React from "react";
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, Easing } from "remotion";
import { z } from "zod";
import CroppedImg from "../components/CroppedImg";
import CroppedVideo from "../components/CroppedVideo";

const isVideoSrc = (src) => {
  if (!src || typeof src !== "string") return false;
  const clean = src.split(/[?#]/)[0];
  return /\.(mp4|webm|mov|m4v|avi|mkv|ogv)$/i.test(clean);
};

const normalizeMediaItem = (item) => {
  if (typeof item === "string") {
    return { src: item, type: isVideoSrc(item) ? "video" : "image" };
  }

  const src = item?.src || "";
  const type = item?.type || (isVideoSrc(src) ? "video" : "image");

  return {
    ...item,
    src,
    type,
  };
};

// Zod schema for ImageSlideshow props
const ImageSlideshowPropsSchema = z.object({
  images: z.array(
    z.object({
      src: z.string().describe("Image path (local file or URL)"),
      focalPoint: z.object({
        x: z.number().min(0).max(100).default(50),
        y: z.number().min(-100).max(200).default(50)
      }).optional().describe("Focal point for this image {x: 0-100, y: 0-100}"),
      panDirection: z.enum(['left-to-right', 'right-to-left', 'top-to-bottom', 'bottom-to-top', 'random']).optional().describe("Pan direction for this image"),
      visibleTime: z.number().optional().describe("Override visible time for this specific image"),
      zoomLevel: z.number().min(1).max(3).optional().describe("Override zoom level for this specific image"),
      type: z.enum(["image", "video"]).optional().describe("Optional explicit media type"),
      crop: z.object({
        x: z.number(),
        y: z.number(),
        width: z.number(),
        height: z.number(),
        sourceWidth: z.number().optional(),
        sourceHeight: z.number().optional(),
      }).nullable().optional().describe("Optional crop data in source pixels"),
      videoStartFrom: z.number().optional().describe("Optional start trim in frames for video"),
      videoEndAt: z.number().optional().describe("Optional end trim in frames for video"),
    })
  ).describe("Array of image objects with configuration"),
  visibleTime: z.number().default(90).describe("Default duration each image is visible (in frames)"),
  zoomLevel: z.number().min(1).max(3).default(1.2).describe("Default zoom level (1 = no zoom, >1 = zoomed in)"),
  transitionDuration: z.number().default(15).describe("Duration of transition between images (in frames)"),
  enableTransitions: z.boolean().default(true).describe("Whether to use fade transitions"),
  panIntensity: z.number().min(0).max(1).default(0.8).describe("How much to pan (0-1, where 1 is maximum possible without showing boundaries)"),
});

export { ImageSlideshowPropsSchema };

/**
 * ImageSlideshow Component
 * - Displays an array of image objects with configurable panning effects
 * - Supports both local files (from public folder) and remote URLs
 * - Each image object contains: src, optional focalPoint, optional panDirection, optional visibleTime, optional zoomLevel
 * - Pan directions: left-to-right, right-to-left, top-to-bottom, bottom-to-top, or random
 * - Focal points take priority over pan directions when both are specified
 * - Panning never exposes beyond image boundaries
 * - Optional fade transitions between images
 */
export const ImageSlideshow = ({
  images = [],
  visibleTime = 90,
  zoomLevel = 1.2,
  transitionDuration = 15,
  enableTransitions = true,
  panIntensity = 0.8,
}) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const normalizedImages = images.map(normalizeMediaItem);

  if (normalizedImages.length === 0) {
    return (
      <AbsoluteFill style={{ backgroundColor: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "white", fontSize: "48px", textAlign: "center" }}>
          No images provided
        </div>
      </AbsoluteFill>
    );
  }

  // Calculate which image should be visible at current frame
  const totalFramesPerImage = visibleTime + (enableTransitions ? transitionDuration : 0);
  const currentImageIndex = Math.floor(frame / totalFramesPerImage) % normalizedImages.length;
  const nextImageIndex = (currentImageIndex + 1) % normalizedImages.length;
  const frameInCurrentImage = frame % totalFramesPerImage;

  // Generate consistent random values for each image based on index
  const getRandomValues = (index) => {
    const imageObj = normalizedImages[index];
    const currentZoom = imageObj.zoomLevel || zoomLevel;
    
    // If this image has a focal point defined
    if (imageObj.focalPoint) {
      const focal = imageObj.focalPoint;
      
      // Calculate maximum pan distance to avoid showing boundaries
      const maxPanX = ((currentZoom - 1) * width) / 2;
      const maxPanY = ((currentZoom - 1) * height) / 2;
      
      // Convert focal point percentages to pixel positions
      // focal.x = 50 means center (0 pan), focal.x = 0 means focus on left (positive pan), etc.
      const focusX = ((50 - focal.x) / 50) * maxPanX * panIntensity;
      const focusY = ((50 - focal.y) / 50) * maxPanY * panIntensity;
      
      // Create subtle panning around the focal point (optional gentle drift)
      const driftAmount = 0.3; // How much to drift from focal point during animation
      const startX = focusX + (Math.sin(index) * maxPanX * driftAmount * panIntensity);
      const endX = focusX + (Math.cos(index) * maxPanX * driftAmount * panIntensity);
      const startY = focusY + (Math.cos(index + 1) * maxPanY * driftAmount * panIntensity);
      const endY = focusY + (Math.sin(index + 1) * maxPanY * driftAmount * panIntensity);
      
      return { startX, endX, startY, endY };
    }
    
    // Pan direction and random panning logic
    // Use image index as seed for consistent randomness
    const seed = index * 12345;
    const random1 = ((seed * 9301 + 49297) % 233280) / 233280;
    const random2 = (((seed + 1) * 9301 + 49297) % 233280) / 233280;
    
    // Determine pan direction
    let panDirection;
    if (imageObj.panDirection) {
      const customDirection = imageObj.panDirection;
      switch (customDirection) {
        case 'left-to-right': panDirection = 0; break;
        case 'right-to-left': panDirection = 1; break;
        case 'top-to-bottom': panDirection = 2; break;
        case 'bottom-to-top': panDirection = 3; break;
        case 'random':
        default:
          panDirection = Math.floor(random1 * 4);
          break;
      }
    } else {
      // Original random logic: (0: left-right, 1: right-left, 2: top-bottom, 3: bottom-top)
      panDirection = Math.floor(random1 * 4);
    }
    
    // Calculate maximum pan distance to avoid showing boundaries
    const maxPanX = ((currentZoom - 1) * width) / 2;
    const maxPanY = ((currentZoom - 1) * height) / 2;
    
    let startX = 0, endX = 0, startY = 0, endY = 0;
    
    switch (panDirection) {
      case 0: // left to right
        startX = -maxPanX * panIntensity;
        endX = maxPanX * panIntensity;
        startY = endY = (random2 - 0.5) * maxPanY * panIntensity;
        break;
      case 1: // right to left
        startX = maxPanX * panIntensity;
        endX = -maxPanX * panIntensity;
        startY = endY = (random2 - 0.5) * maxPanY * panIntensity;
        break;
      case 2: // top to bottom
        startY = -maxPanY * panIntensity;
        endY = maxPanY * panIntensity;
        startX = endX = (random2 - 0.5) * maxPanX * panIntensity;
        break;
      case 3: // bottom to top
        startY = maxPanY * panIntensity;
        endY = -maxPanY * panIntensity;
        startX = endX = (random2 - 0.5) * maxPanX * panIntensity;
        break;
    }
    
    return { startX, endX, startY, endY };
  };

  // Calculate continuous pan position for each image based on global frame
  const getPanPosition = (imageIndex, globalFrame) => {
    const imageObj = normalizedImages[imageIndex];
    const imageVisibleTime = imageObj?.visibleTime || visibleTime;
    const imageStartFrame = imageIndex * totalFramesPerImage;
    const imageEndFrame = imageStartFrame + imageVisibleTime;
    const pan = getRandomValues(imageIndex);
    
    // Calculate pan progress based on global timeline, not per-image timeline
    const panProgress = interpolate(
      globalFrame,
      [imageStartFrame, imageEndFrame],
      [0, 1],
      {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      }
    );
    
    const x = interpolate(panProgress, [0, 1], [pan.startX, pan.endX], {
      easing: Easing.inOut(Easing.ease),
    });
    const y = interpolate(panProgress, [0, 1], [pan.startY, pan.endY], {
      easing: Easing.inOut(Easing.ease),
    });
    
    return { x, y };
  };

  // Get pan values for current and next images
  const currentPan = getPanPosition(currentImageIndex, frame);
  const nextPan = getPanPosition(nextImageIndex, frame);

  // Calculate transition opacity
  let currentOpacity = 1;
  let nextOpacity = 0;
  
  if (enableTransitions && frameInCurrentImage >= visibleTime) {
    const transitionProgress = (frameInCurrentImage - visibleTime) / transitionDuration;
    currentOpacity = interpolate(transitionProgress, [0, 1], [1, 0], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });
    nextOpacity = interpolate(transitionProgress, [0, 1], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });
  }

  // Get current image settings
  const currentImage = normalizedImages[currentImageIndex];
  const nextImage = normalizedImages[nextImageIndex];
  const currentZoom = currentImage?.zoomLevel || zoomLevel;
  const nextZoom = nextImage?.zoomLevel || zoomLevel;
  const currentSrc = currentImage?.src;
  const nextSrc = nextImage?.src;

  return (
    <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
      {/* Current Image */}
      <AbsoluteFill style={{ opacity: currentOpacity }}>
        {currentSrc ? (
          currentImage?.type === "video" ? (
            <CroppedVideo
              src={currentSrc}
              crop={currentImage?.crop}
              startFrom={currentImage?.videoStartFrom}
              endAt={currentImage?.videoEndAt}
              muted
              style={{
                width: width * currentZoom,
                height: height * currentZoom,
                objectFit: "cover",
                transform: `translate(${currentPan.x}px, ${currentPan.y}px)`,
                position: "absolute",
                top: -((currentZoom - 1) * height) / 2,
                left: -((currentZoom - 1) * width) / 2,
              }}
            />
          ) : (
            <CroppedImg
              src={currentSrc}
              crop={currentImage?.crop}
              style={{
                width: width * currentZoom,
                height: height * currentZoom,
                objectFit: "cover",
                transform: `translate(${currentPan.x}px, ${currentPan.y}px)`,
                position: "absolute",
                top: -((currentZoom - 1) * height) / 2,
                left: -((currentZoom - 1) * width) / 2,
              }}
            />
          )
        ) : null}
      </AbsoluteFill>

      {/* Next Image (for transition) */}
      {enableTransitions && nextOpacity > 0 && (
        <AbsoluteFill style={{ opacity: nextOpacity }}>
          {nextSrc ? (
            nextImage?.type === "video" ? (
              <CroppedVideo
                src={nextSrc}
                crop={nextImage?.crop}
                startFrom={nextImage?.videoStartFrom}
                endAt={nextImage?.videoEndAt}
                muted
                style={{
                  width: width * nextZoom,
                  height: height * nextZoom,
                  objectFit: "cover",
                  transform: `translate(${nextPan.x}px, ${nextPan.y}px)`,
                  position: "absolute",
                  top: -((nextZoom - 1) * height) / 2,
                  left: -((nextZoom - 1) * width) / 2,
                }}
              />
            ) : (
              <CroppedImg
                src={nextSrc}
                crop={nextImage?.crop}
                style={{
                  width: width * nextZoom,
                  height: height * nextZoom,
                  objectFit: "cover",
                  transform: `translate(${nextPan.x}px, ${nextPan.y}px)`,
                  position: "absolute",
                  top: -((nextZoom - 1) * height) / 2,
                  left: -((nextZoom - 1) * width) / 2,
                }}
              />
            )
          ) : null}
        </AbsoluteFill>
      )}


    </AbsoluteFill>
  );
};

export default ImageSlideshow;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
